<script setup lang="ts">
import { useTrackedUploads, type UploadStage } from '@/composables/useTrackedUpload'

const { activeUploads, cancelTrackedUpload } = useTrackedUploads()

const labels: Record<UploadStage, string> = {
  sending: 'Uploading',
  preparing: 'Preparing',
  uploading: 'Uploading',
  scanning: 'Scanning',
  importing: 'Importing',
  converting: 'Converting to AVIF',
  compressing: 'Compressing',
  optimizing: 'Converting to AVIF and optimizing',
  saving: 'Saving',
  finalizing: 'Finalizing',
  done: 'Done',
  failed: 'Failed',
  canceled: 'Canceled',
}

function displayKind(kind: string): string {
  return kind.replace(/[-_]+/g, ' ').replace(/\b\w/g, value => value.toUpperCase())
}
</script>

<template>
  <aside v-if="activeUploads.length" class="upload-stack" aria-live="polite">
    <article v-for="upload in activeUploads" :key="upload.id" class="upload-progress-card" :class="upload.stage">
      <div class="upload-progress-line">
        <strong>{{ displayKind(upload.kind) }}</strong>
        <span class="upload-stage">{{ labels[upload.stage] }}</span>
        <p v-if="upload.networkProgress !== null">{{ upload.networkProgress }}%</p>
        <p v-else-if="upload.processingProgress !== null">{{ upload.processingProgress }}%</p>
        <p v-else-if="upload.error" :title="upload.error">{{ upload.error }}</p>
        <span v-else class="upload-spacer"></span>
        <span v-if="!['done', 'failed', 'canceled'].includes(upload.stage)" class="upload-cancel">
          <i aria-hidden="true">|</i>
          <button type="button" @click="cancelTrackedUpload(upload.id)">Cancel</button>
        </span>
      </div>
      <div v-if="upload.networkProgress !== null" class="upload-progress-track"><i :style="{ width: `${upload.networkProgress}%` }"></i></div>
    </article>
  </aside>
</template>

<style scoped>
.upload-stack { position: fixed; right: 12px; bottom: 12px; z-index: 100005; width: max-content; min-width: min(340px,calc(100vw - 24px)); max-width: calc(100vw - 24px); display: grid; gap: 7px; }
.upload-progress-card { position: relative; min-width: 0; min-height: 36px; padding: 9px 10px 10px; border: 1px solid #3d4650; border-radius: 5px; background: rgba(15,17,20,.97); box-shadow: 0 5px 22px rgba(0,0,0,.5); color: #d3d8df; font: 12px Inconsolata,monospace; }
.upload-progress-line { display: flex; align-items: center; gap: 7px; min-width: 0; max-width: calc(100vw - 44px); flex-wrap: wrap; }
.upload-progress-line strong { min-width: 0; flex: none; white-space: nowrap; }
.upload-stage { min-width: 0; flex: 0 1 auto; color: #83c7e8; white-space: nowrap; }
.upload-spacer { flex: 1; }
.upload-progress-line p { min-width: 0; max-width: 100%; flex: 0 1 auto; margin: 0; color: #89939f; overflow-wrap: anywhere; }
.upload-cancel { display: inline-flex; flex: none; align-items: center; gap: 7px; white-space: nowrap; }
.upload-cancel > i { color: #58616c; font-style: normal; }
.upload-progress-track { position: absolute; right: 0; bottom: 0; left: 0; height: 3px; overflow: hidden; background: #2b3036; }
.upload-progress-track i { display: block; height: 100%; background: #489cd6; transition: width .12s linear; }
.upload-progress-card button { flex: none; padding: 1px 0; border: 0; background: none; color: #d87878; cursor: pointer; }
.upload-progress-card.done { border-color: #35664a; }
.upload-progress-card.failed { border-color: #7c3030; }
</style>
